import { TILE_QUOTA, WALK_ORDER, FLOOR_SLOTS } from './tokens';
// One-way: counterList.ts never imports this file, so the veto keys can live with the rest of the
// 選品 arithmetic while the labels stay here with variantLabel.
import { isVariantVetoed, variantKeepSet, variantVetoSet } from './counterList';
import {
  BringBackPhase, DoneKind, RestockV2CapacitySource, RestockV2Fit, RestockV2Fixture,
  RestockV2Line, RestockV2ListVariant, RestockV2Move, RestockV2Order, RestockV2OrderLine,
  RestockV2OrderTotals,
  RestockV2OrderVariant, RestockV2Plan, RestockV2ParamKey, RestockV2PlanRow, RestockV2Racks,
  RestockV2ThumbTarget, RestockV2Tile, SeasonKey, TierKey, TileState, TodayGroupKey, WhyStatus,
} from './types';

// Same error envelopes as components/noke/restockPlan.ts readNokeError.
export function readNokeError(error: unknown, fallback: string): string {
  const err = error as { message?: string; response?: { data?: { message?: string; error?: string | { message?: string } } } } | null | undefined;
  const data = err?.response?.data;
  const nested = data?.error && typeof data.error === 'object' ? data.error.message : undefined;
  return [nested, data?.message, typeof data?.error === 'string' ? data.error : undefined, err?.message]
    .find((message): message is string => typeof message === 'string' && message.trim().length > 0) || fallback;
}

export function newReference(prefix: string): string {
  const unique = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${unique}`;
}

const TITLE_PREFIXES = [/^\s*【Metanoia】\s*/, /^\s*\[MdoC\]\s*/i, /^\s*🇰🇷?\s*/, /^\s*韓製\s*/, /^\s*韓國設計\s*/];

// The endpoint already ships a stripped title; strip again so a raw title never reaches the counter screen.
export function displayTitle(title?: string | null): string {
  let text = (title || '').trim();
  for (let pass = 0; pass <= TITLE_PREFIXES.length; pass += 1) {
    let changed = false;
    for (let index = 0; index < TITLE_PREFIXES.length; index += 1) {
      const next = text.replace(TITLE_PREFIXES[index], '');
      if (next !== text) { text = next; changed = true; }
    }
    if (!changed) break;
  }
  return text.trim();
}

// ---- ⚙ settings -----------------------------------------------------------------------------
// The v3 parameter set (restock_v3_season_logic.md, "⚙ settings"). `capacity` and `aw_target_pct`
// may be left blank, which is why they are not in PARAM_KEYS.
export const PARAM_KEYS: RestockV2ParamKey[] = [
  'cover_days', 'lead_budget', 'new_styles_per_week', 'pieces_per_new_style', 'newness_days', 'ss_core_depth_cap',
];
export const OPTIONAL_PARAM_KEYS: RestockV2ParamKey[] = ['capacity', 'aw_target_pct'];
export const QUERY_KEYS: RestockV2ParamKey[] = PARAM_KEYS.concat(OPTIONAL_PARAM_KEYS);

export const PARAM_RANGE: Record<RestockV2ParamKey, { min: number; max: number }> = {
  cover_days: { min: 1, max: 60 },
  lead_budget: { min: 0, max: 200 },
  new_styles_per_week: { min: 0, max: 30 },
  pieces_per_new_style: { min: 1, max: 3 },
  newness_days: { min: 7, max: 120 },
  ss_core_depth_cap: { min: 0, max: 6 },
  capacity: { min: 1, max: 1000 },
  aw_target_pct: { min: 0, max: 100 },
};

// Used when the endpoint has not echoed a parameter back yet, so the dialog is never blank-and-invalid.
export const PARAM_DEFAULT: Record<RestockV2ParamKey, number> = {
  cover_days: 14, lead_budget: 6, new_styles_per_week: 8, pieces_per_new_style: 1,
  newness_days: 45, ss_core_depth_cap: 2, capacity: 150, aw_target_pct: 50,
};

export const PARAM_LABEL: Record<RestockV2ParamKey, string> = {
  cover_days: '備貨天數',
  lead_budget: '線索款預算（款）',
  new_styles_per_week: '每週新季款款數',
  pieces_per_new_style: '試賣每款幾組',
  newness_days: '算新款的天數',
  ss_core_depth_cap: '夏款主力件數上限',
  capacity: '容量（件）',
  aw_target_pct: '秋款目標比例（%）',
};

export const PARAM_HELP: Record<RestockV2ParamKey, string> = {
  cover_days: '主力款補到幾天的銷量',
  lead_budget: '線索款這次最多幾款',
  new_styles_per_week: '一週最多放幾款新季款上架',
  pieces_per_new_style: '門市還沒賣過的款先放幾個顏色（一組＝一個顏色的主要尺寸）',
  newness_days: '上架幾天內算新季款',
  ss_core_depth_cap: '換季期間夏款主力每色碼最多幾件',
  capacity: '留白＝衣架設定加總',
  aw_target_pct: '留白＝照日曆表算今天的目標',
};

export function paramValid(key: RestockV2ParamKey, input: string): boolean {
  const text = input.trim();
  if (text === '') return OPTIONAL_PARAM_KEYS.indexOf(key) >= 0;
  if (!/^\d+$/.test(text)) return false;
  const value = Number(text);
  const range = PARAM_RANGE[key];
  return Number.isSafeInteger(value) && value >= range.min && value <= range.max;
}

export function paramRangeNote(key: RestockV2ParamKey): string {
  const range = PARAM_RANGE[key];
  const blank = OPTIONAL_PARAM_KEYS.indexOf(key) >= 0 ? '，或留白' : '';
  return `請輸入 ${range.min}–${range.max} 的整數${blank}`;
}

const CAPACITY_SOURCE_LABEL: Record<RestockV2CapacitySource, string> = {
  override: '本次手動設定', fixtures: '衣架設定加總', default: '預設值',
};

export function capacitySourceLabel(source?: RestockV2CapacitySource | null): string {
  return source ? CAPACITY_SOURCE_LABEL[source] || String(source) : '';
}

// ---- today groups ---------------------------------------------------------------------------
export const TODAY_GROUPS: TodayGroupKey[] = ['display', 'from_office', 'bring_back', 'waiting'];

export const TODAY_KIND: Record<TodayGroupKey, DoneKind> = {
  display: 'display', from_office: 'from_office', bring_back: 'bring_back', waiting: 'received',
};

export const TODAY_LABEL: Record<TodayGroupKey, string> = {
  display: '上架', from_office: '從辦公室拿', bring_back: '帶回辦公室', waiting: '等到貨',
};

// ---- tiers ----------------------------------------------------------------------------------
// `new_season` renamed `seasonal` in the v3 API; read both so a lagging backend still renders.
export function tierLines(order: RestockV2Order | undefined, tier: TierKey): RestockV2OrderLine[] {
  const tiers = order?.tiers || {};
  if (tier === 'new_season') return tiers.new_season || tiers.seasonal || [];
  return tiers[tier] || [];
}

export function tierTotal(totals: RestockV2OrderTotals | undefined, tier: TierKey): number {
  if (!totals) return 0;
  if (tier === 'new_season') return Number(totals.new_season ?? totals.seasonal ?? 0) || 0;
  return Number(totals[tier as 'core' | 'lead'] ?? 0) || 0;
}

export const FLAG_LABEL: Record<string, string> = {
  v1_season_blocked: '舊版季節規則會擋掉這件', preferred: '你指定',
};

// 'preferred' is Magnus's own pick, not a warning — keep it visually apart from the blocked flag.
export function flagColor(flag: string): 'primary' | 'error' {
  return flag === 'preferred' ? 'primary' : 'error';
}

export const WHY_STATUS_LABEL: Record<WhyStatus, string> = {
  core: '主力', lead: '線索款', new_season: '新季款', seasonal: '新季款', on_rail_ok: '已在架上，數量夠',
  not_selected: '這次沒選上', blocked: '目前無法補貨', unknown: '查不到這個 SKU',
};

export const NOW_SOURCE_NOTE = '位置為推估（尚無陳列紀錄）';

// ---- dates ----------------------------------------------------------------------------------
const WEEKDAYS = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];

// Dates arrive as plain Taipei calendar days ("2026-09-16"); parse the parts so a browser
// west of UTC never shifts the order night to the day before.
export function parseYmd(value?: string | null): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) { const loose = new Date(value); return Number.isNaN(loose.getTime()) ? null : loose; }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function monthDay(value?: string | null): string {
  const date = parseYmd(value);
  return date ? `${date.getMonth() + 1}/${date.getDate()}` : '待定';
}

export function weekdayLabel(value?: string | null): string {
  const date = parseYmd(value);
  return date ? WEEKDAYS[date.getDay()] : '';
}

export function isoMonthDay(value?: string | null): string {
  if (!value) return '待定';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '待定' : `${date.getMonth() + 1}/${date.getDate()}`;
}

export function addDays(value: string | null | undefined, days: number): string {
  if (!value) return '待定';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '待定';
  date.setDate(date.getDate() + days);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

// ---- row labels -----------------------------------------------------------------------------
export function variantLabel(color?: string | null, size?: string | null): string {
  return [color, size].filter((part): part is string => !!part && part.trim().length > 0).join(' ');
}

export function lineQtyLabel(line: RestockV2Line): string {
  const label = variantLabel(line.color, line.size);
  return label ? `${label} ×${line.qty}` : `×${line.qty}`;
}

export function whereLabel(line: RestockV2Line): string {
  const parts: string[] = [];
  if (line.fixture_label) parts.push(`→ ${line.fixture_label}`);
  if (line.storage_slot) parts.push(`位置 ${line.storage_slot}`);
  return parts.join(' · ');
}

// 「黑色 L」 on a 我的清單 chip. A style whose single variant carries no colour and no size still
// needs a chip to hang the × on, and 訂貨 already calls that case 單一款 — same word, same meaning.
export function variantChipLabel(variant: RestockV2ListVariant): string {
  return variantLabel(variant.color, variant.size) || '單一款';
}

export function orderVariantChip(variant: RestockV2OrderVariant): string {
  const label = variantLabel(variant.color, variant.size);
  const move = `${variant.on_hand}→${variant.target}`;
  return label ? `${label} ${move}` : move;
}

export function priceLabel(price?: number | null): string {
  const value = Number(price);
  return Number.isFinite(value) && value > 0 ? `NT$${value.toLocaleString('en-US')}` : '';
}

export function season(value?: SeasonKey | null): SeasonKey | null {
  return value === 'AW' || value === 'SS' || value === 'ALL' ? value : null;
}

// ---- thumbnails -----------------------------------------------------------------------------
export function thumbTarget(row: {
  sku: string; title: string; image_url?: string | null; variant_id?: number | null;
}): RestockV2ThumbTarget {
  return { sku: row.sku, title: row.title, image_url: row.image_url ?? null, variant_id: row.variant_id ?? null };
}

// An order line has no variant_id of its own; the gallery needs one, so borrow the first variant's.
export function orderThumbTarget(line: RestockV2OrderLine): RestockV2ThumbTarget {
  const variantId = (line.variants || [])
    .map((variant) => variant.variant_id)
    .find((id): id is number => typeof id === 'number');
  return { sku: line.sku, title: line.title, image_url: line.image_url ?? null, variant_id: variantId ?? null };
}

// SKU → picture, built from every row that does carry one, so the rack tiles (which may carry none)
// can still show the garment. First non-null image per SKU wins.
export function skuImageIndex(plan: RestockV2Plan | null | undefined): Record<string, RestockV2ThumbTarget> {
  const index: Record<string, RestockV2ThumbTarget> = {};
  const add = (target: RestockV2ThumbTarget) => {
    if (!target.sku) return;
    const current = index[target.sku];
    if (current?.image_url) return;
    index[target.sku] = {
      sku: target.sku,
      title: target.title || current?.title || target.sku,
      image_url: target.image_url ?? current?.image_url ?? null,
      variant_id: target.variant_id ?? current?.variant_id ?? null,
    };
  };
  TODAY_GROUPS.forEach((key) => (plan?.today?.[key] || []).forEach((line) => add(thumbTarget(line))));
  const orderLines: RestockV2OrderLine[] = [];
  (['core', 'new_season', 'lead'] as TierKey[]).forEach((tier) => orderLines.push(...tierLines(plan?.order, tier)));
  orderLines.push(...(plan?.order?.warehouse || []));
  (plan?.order?.groups || []).forEach((group) => orderLines.push(...(group.lines || [])));
  orderLines.forEach((line) => add(orderThumbTarget(line)));
  (plan?.racks?.fixtures || []).forEach((fixture) => {
    (fixture.tiles || []).forEach((tile) => add(thumbTarget(tile)));
    (fixture.keep || []).forEach((item) => add(thumbTarget(item)));
    (fixture.add || []).forEach((item) => add(thumbTarget(item)));
    (fixture.remove || []).forEach((item) => add(thumbTarget(item)));
  });
  return index;
}

// The row's own picture wins; the index only fills the gap.
export function indexedThumbTarget(
  index: Record<string, RestockV2ThumbTarget>,
  row: { sku: string; title: string; image_url?: string | null; variant_id?: number | null },
): RestockV2ThumbTarget {
  const found = index[row.sku];
  return {
    sku: row.sku,
    title: row.title || found?.title || row.sku,
    image_url: row.image_url ?? found?.image_url ?? null,
    variant_id: row.variant_id ?? found?.variant_id ?? null,
  };
}

// ---- counting -------------------------------------------------------------------------------
export function pieces(lines: { qty: number }[] | undefined): number {
  return (lines || []).reduce((total, line) => total + (Number(line.qty) || 0), 0);
}

export function orderPieces(lines: RestockV2OrderLine[] | undefined): number {
  return (lines || []).reduce((total, line) => total + (Number(line.order_qty) || 0), 0);
}

// The strip says 「帶回 N 件後 M 件」 — N is both phases together, M is the engine's own end_state.
export function fitBringBackTotal(fit?: RestockV2Fit | null): number {
  return (Number(fit?.bring_back_now) || 0) + (Number(fit?.bring_back_on_arrival) || 0);
}

export function todayTotal(plan: RestockV2Plan | null): number {
  if (!plan) return 0;
  const totals = plan.today?.totals;
  return TODAY_GROUPS.reduce((sum, key) => sum + (Number(totals?.[key]) || 0), 0);
}

// Rows with no phase are treated as 「先帶回」 so a backend that has not shipped phases yet still shows them.
export function bringBackPhase(lines: RestockV2Line[] | undefined, phase: BringBackPhase): RestockV2Line[] {
  return (lines || []).filter((line) => (line.phase || 'now') === phase);
}

export function bringBackSectionLabel(phase: BringBackPhase, qty: number, capacity?: number | null): string {
  return phase === 'now' ? `先帶回 ${qty} 件（騰到容量 ${capacity ?? 0}）` : `到貨那天再帶回 ${qty} 件`;
}

// ---- the walk (design B's 今天) ---------------------------------------------------------------
const UNPLACED = '__unplaced';
const UNPLACED_LABEL = '未指定位置';

function fixtureKey(line: RestockV2Line): string {
  return line.fixture_id || UNPLACED;
}

// Fixtures in the order a part-timer walks them: the known floor ids first, anything the backend
// invented afterwards, so a new fixture shows up instead of vanishing.
export function walkFixtureIds(plan: RestockV2Plan | null | undefined): string[] {
  const present = (plan?.racks?.fixtures || []).map((fixture) => fixture.id);
  const ordered = WALK_ORDER.filter((id) => present.indexOf(id) >= 0);
  present.forEach((id) => { if (ordered.indexOf(id) < 0) ordered.push(id); });
  // A row can point at a fixture the racks list does not carry — keep it reachable.
  TODAY_GROUPS.forEach((group) => (plan?.today?.[group] || []).forEach((line) => {
    const id = fixtureKey(line);
    if (ordered.indexOf(id) < 0) ordered.push(id);
  }));
  return ordered;
}

// Fixtures in floor-plan order (the grid slots first, unknown ids appended full width).
export function floorFixtures(racks: RestockV2Racks | undefined): RestockV2Fixture[] {
  const fixtures = racks?.fixtures || [];
  const known = Object.keys(FLOOR_SLOTS);
  const placed = known
    .map((id) => fixtures.filter((fixture) => fixture.id === id)[0])
    .filter((fixture): fixture is RestockV2Fixture => !!fixture);
  const rest = fixtures.filter((fixture) => known.indexOf(fixture.id) < 0);
  return placed.concat(rest);
}

export function fixtureLabelOf(plan: RestockV2Plan | null | undefined, id: string): string {
  const fixture = (plan?.racks?.fixtures || []).filter((row) => row.id === id)[0];
  if (fixture) return fixture.label;
  const labelled = TODAY_GROUPS
    .reduce<RestockV2Line[]>((rows, group) => rows.concat(plan?.today?.[group] || []), [])
    .filter((line) => fixtureKey(line) === id && !!line.fixture_label)[0];
  return labelled?.fixture_label || (id === UNPLACED ? UNPLACED_LABEL : id);
}

// Every physical thing to do today, already in walk order, so the flat list IS the route.
// on_arrival 帶回 and 等到貨 receipts are appended and flagged `counted: false` — they are not part
// of 「n / N 件事」 (R4/R6: they need the Korea box to land first).
export function buildMoves(plan: RestockV2Plan | null | undefined): RestockV2Move[] {
  const moves: RestockV2Move[] = [];
  const push = (kind: RestockV2Move['kind'], group: TodayGroupKey, line: RestockV2Line, counted: boolean) => {
    const id = `${kind}:${line.sku}:${line.variant_id ?? 'x'}:${moves.length}`;
    moves.push({
      id,
      kind,
      group,
      line,
      fixtureId: fixtureKey(line),
      fixtureLabel: line.fixture_label || fixtureLabelOf(plan, fixtureKey(line)),
      counted,
    });
  };
  const ids = walkFixtureIds(plan);
  const display = plan?.today?.display || [];
  const fromOffice = plan?.today?.from_office || [];
  const bringBack = plan?.today?.bring_back || [];
  // 2026-10-04 (rule 1, office before Korea): 去拿 leads every rack — those pieces are already
  // paid for and fill the holes on the rail today; 上架 and 帶回 follow.
  ids.forEach((id) => {
    fromOffice.filter((line) => fixtureKey(line) === id).forEach((line) => push('from_office', 'from_office', line, true));
    display.filter((line) => fixtureKey(line) === id).forEach((line) => push('display', 'display', line, true));
    bringBackPhase(bringBack, 'now').filter((line) => fixtureKey(line) === id)
      .forEach((line) => push('bring_back', 'bring_back', line, true));
  });
  ids.forEach((id) => {
    bringBackPhase(bringBack, 'on_arrival').filter((line) => fixtureKey(line) === id)
      .forEach((line) => push('on_arrival', 'bring_back', line, false));
  });
  (plan?.today?.waiting || []).forEach((line) => push('waiting', 'waiting', line, false));
  return moves;
}

export function countedMoves(moves: RestockV2Move[]): RestockV2Move[] {
  return moves.filter((move) => move.counted);
}

export function movesOfFixture(moves: RestockV2Move[], fixtureId: string, counted: boolean): RestockV2Move[] {
  return moves.filter((move) => move.fixtureId === fixtureId && move.counted === counted);
}

export interface ZoneCounts {
  display: number;
  from_office: number;
  bring_back: number;
  waiting: number;
}

// The 上／拿／回／等 pills on a map zone, in pieces (what the staff carries), not in cards.
export function zoneCounts(moves: RestockV2Move[], fixtureId: string): ZoneCounts {
  const counts: ZoneCounts = { display: 0, from_office: 0, bring_back: 0, waiting: 0 };
  moves.filter((move) => move.fixtureId === fixtureId).forEach((move) => {
    const qty = Number(move.line.qty) || 0;
    if (move.kind === 'display') counts.display += qty;
    else if (move.kind === 'from_office') counts.from_office += qty;
    else if (move.kind === 'bring_back') counts.bring_back += qty;
    else counts.waiting += qty;
  });
  return counts;
}

export function zoneIsClear(counts: ZoneCounts): boolean {
  return !counts.display && !counts.from_office && !counts.bring_back && !counts.waiting;
}

// The headline on a walk card: the colour is how you recognise it on the rail; with no colour
// (a bottle, a belt) the product name IS the headline.
export function moveHeadline(line: RestockV2Line, garment: boolean): string {
  if (!line.color) return displayTitle(line.title) || line.sku;
  const size = line.size && (garment || line.size !== 'F') ? line.size : '';
  return size ? `${line.color} · ${size}` : line.color;
}

export function isGarmentFixture(fixtureId: string): boolean {
  return ['pants_a', 'pants_b', 'tops_left', 'tops_right'].indexOf(fixtureId) >= 0;
}

// ---- rack tiles -----------------------------------------------------------------------------
// The endpoint ships `tiles`; until it does, build the same picture out of keep/add/remove plus the
// 今天 上架 rows (which are exactly the "門市有貨，架上少了" gaps).
//
// 不要這個顏色／尺寸 is honoured HERE, at the one chokepoint both rack surfaces go through (the
// desktop map's right column and the phone's full-screen sheet), so the two cannot diverge.
//
// The rule is NOT 「hide vetoed variants」 — it is 「never OFFER one」. A 缺／✓／到貨中 tile says
// 「this colour belongs on this rail」 and a vetoed colour never does. A 'remove' tile says the
// opposite — 「take this off」 — and a vetoed colour that is physically on the rail is exactly the
// thing that must come off; hiding it would strand stock he already paid for, which is the same
// defect as the 09-16 gate that hid owned pieces. So: drop vetoed tiles unless they are 'remove'.
// A tile with no `variant_id` is never matched — a veto is a colour/size, not a style.
export function fixtureTiles(fixture: RestockV2Fixture, plan: RestockV2Plan | null | undefined): RestockV2Tile[] {
  const vetoed = variantVetoSet(plan?.list || null);
  const pinned = variantKeepSet(plan?.list || null);
  const live = (tile: RestockV2Tile) => (tile.state || 'ok') === 'remove'
    || !(tile.vetoed === true || isVariantVetoed(vetoed, tile.sku, tile.variant_id));
  // A 留著 is read from the tile OR from the list, so the pin shows even on a backend whose tiles
  // do not carry `kept` yet. Never the other way round: a list can only ADD a pin, never clear one
  // the tile asserts, and a vetoed variant is never pinned (one decision row, 不要 wins).
  const withKeep = (tile: RestockV2Tile): RestockV2Tile => {
    if (tile.vetoed === true || isVariantVetoed(vetoed, tile.sku, tile.variant_id)) return tile;
    if (tile.kept === true || !isVariantVetoed(pinned, tile.sku, tile.variant_id)) return tile;
    return { ...tile, kept: true, state: (tile.state || 'ok') === 'remove' ? 'ok' : tile.state };
  };
  if (fixture.tiles && fixture.tiles.length) return fixture.tiles.filter(live).map(withKeep);
  const tiles: RestockV2Tile[] = [];
  // `on_hand` is set on every derived tile too, and only where it is EXACT — 上架 rows carry the
  // engine's own count, 留在架上 is by definition the pieces already hanging, 到貨中 is not on the
  // rail yet (0), and a 帶回 row with no target takes everything off, so its qty IS the shelf count.
  // Nothing here estimates: a made-up stock number is worse than the badge alone.
  (plan?.today?.display || []).filter((line) => fixtureKey(line) === fixture.id).forEach((line) => tiles.push({
    sku: line.sku, title: line.title, image_url: line.image_url ?? null, color: line.color,
    size: line.size, season: season(line.season), state: 'short', qty: line.qty, target: line.qty,
    variant_id: line.variant_id, on_hand: line.on_hand, office: line.office, transit: line.transit,
  }));
  (fixture.add || []).forEach((item) => tiles.push({
    sku: item.sku, title: item.title, image_url: item.image_url ?? null, color: null, size: null,
    season: season(item.season), state: 'incoming', qty: item.pieces, target: item.pieces,
    variant_id: item.variant_id ?? null, on_hand: 0,
  }));
  (fixture.keep || []).forEach((item) => tiles.push({
    sku: item.sku, title: item.title, image_url: item.image_url ?? null, color: null, size: null,
    season: season(item.season), state: 'ok', qty: item.pieces, target: item.pieces,
    variant_id: item.variant_id ?? null, on_hand: item.pieces,
  }));
  (fixture.remove || []).forEach((item) => tiles.push({
    sku: item.sku, title: item.title, image_url: item.image_url ?? null, color: item.color,
    size: item.size, season: season(item.season), state: 'remove', qty: item.qty, target: null,
    variant_id: item.variant_id ?? null, on_hand: item.qty,
  }));
  return tiles.filter(live).map(withKeep);
}

// Balanced first screen: at most TILE_QUOTA of each state, the rest behind 「全部展開」.
export function splitTiles(tiles: RestockV2Tile[]): { shown: RestockV2Tile[]; rest: RestockV2Tile[] } {
  const seen: Record<TileState, number> = { short: 0, ok: 0, remove: 0, incoming: 0 };
  const shown: RestockV2Tile[] = [];
  const rest: RestockV2Tile[] = [];
  tiles.forEach((tile) => {
    const state: TileState = tile.state || 'ok';
    seen[state] = (seen[state] || 0) + 1;
    (seen[state] <= (TILE_QUOTA[state] ?? 6) ? shown : rest).push(tile);
  });
  return { shown, rest };
}

export function tileStates(tiles: RestockV2Tile[]): TileState[] {
  const order: TileState[] = ['short', 'incoming', 'ok', 'remove'];
  return order.filter((state) => tiles.some((tile) => (tile.state || 'ok') === state));
}

export function tileHeadline(tile: RestockV2Tile): string {
  const label = variantLabel(tile.color, tile.size);
  return label || `${tile.qty} 件`;
}

// ---- 架上現在有幾件 --------------------------------------------------------------------------
// Magnus 2026-09-17: 「should show 門市現在有幾件」. The badge on the photo is the ACTION (缺 1 =
// short by one, 帶回 1 = take one off, ✓ = leave it), never a stock count, so the rail could not be
// read off the screen: 缺1 says nothing about whether one piece is hanging there or nine.
//
// Two rules keep the two numbers from ever being read as one:
//   1. different PLACE — the action is a coloured pill on the photo, the stock is a labelled line
//      in the text block. Never two pills.
//   2. the stock number always carries the word 架上 and the unit 件. A bare numeral is what would
//      let them blur.
// And the (2)/(3) inside 「白色 M(2)」 is part of the Korean SIZE STRING — `tileHeadline` passes it
// through untouched and nothing here reads or strips it.
export const SHELF_LABEL = '架上';

/** The stock the engine reports for this tile, or null when this plan does not carry one. */
export function tileOnHand(tile: RestockV2Tile): number | null {
  const value = Number(tile.on_hand);
  return tile.on_hand === null || tile.on_hand === undefined || !Number.isFinite(value) ? null : value;
}

/** 「架上 2 件」 — also the tile's accessible name for that line. 0 is a truthful count. */
export function shelfCountLabel(count: number): string {
  return `${SHELF_LABEL} ${count} 件`;
}

export function removePieces(rows: { qty: number }[] | undefined): number {
  return (rows || []).reduce((total, row) => total + (Number(row.qty) || 0), 0);
}

// ---- copy text ------------------------------------------------------------------------------
function listRow(line: RestockV2Line): string {
  return [`${line.sku} ${displayTitle(line.title)}｜${lineQtyLabel(line)}`, whereLabel(line), line.reason]
    .filter((part) => !!part && String(part).trim().length > 0).join(' · ');
}

// Plain-text list for the per-group 「複製清單」 button; 帶回 keeps its two phase sections.
export function todayListText(key: TodayGroupKey, lines: RestockV2Line[], capacity?: number | null): string {
  const rows = lines || [];
  const out = [`${TODAY_LABEL[key]}（${pieces(rows)} 件）`, ''];
  if (key !== 'bring_back') return out.concat(rows.map(listRow)).join('\n');
  const phases: BringBackPhase[] = ['now', 'on_arrival'];
  phases.forEach((phase) => {
    const section = bringBackPhase(rows, phase);
    if (!section.length) return;
    out.push(bringBackSectionLabel(phase, pieces(section), capacity));
    section.forEach((line) => out.push(listRow(line)));
    out.push('');
  });
  return out.join('\n').replace(/\n+$/, '');
}

// The 一個架子 list the staff copies into LINE while standing in front of it.
export function zoneListText(label: string, moves: RestockV2Move[]): string {
  const out = [`${label}（${moves.length} 件事）`, ''];
  moves.forEach((move) => out.push(listRow(move.line)));
  return out.join('\n');
}

export function warehouseSummary(lines: RestockV2OrderLine[] | undefined): string {
  const rows = lines || [];
  if (!rows.length) return '';
  return `倉庫補：${rows.map((line) => `${line.sku} ${displayTitle(line.title)} ×${line.order_qty}`).join(' · ')}`;
}

// ---- plan versions --------------------------------------------------------------------------
// 1-based version number of a stored plan, oldest first, so the caption reads 「第 N 版使用中」.
export function planVersion(racks: RestockV2Racks | undefined, planId?: number | null): number | null {
  const history = (racks?.history || []).slice().sort((a, b) => a.id - b.id);
  if (!history.length) return null;
  const target = planId ?? history.filter((row) => row.active).map((row) => row.id)[0];
  if (target === undefined || target === null) return null;
  const index = history.map((row) => row.id).indexOf(target);
  return index >= 0 ? index + 1 : history.length;
}

export function historyLabel(row: RestockV2PlanRow, version: number | null): string {
  const stamp = isoMonthDay(row.created_at);
  return [version ? `第 ${version} 版` : null, stamp, row.label || null, row.active ? '（使用中）' : null]
    .filter((part): part is string => !!part).join(' ');
}

// Today's AW target off the ramp, for the ⚙ dialog's placeholder. Linear between points, flat
// outside them — the same reading of R1 the engine uses.
export function rampValueOn(ramp: { date: string; pct: number }[] | undefined, on: Date): number | null {
  const points = (ramp || [])
    .map((point) => ({ at: parseYmd(point.date), pct: Number(point.pct) }))
    .filter((point): point is { at: Date; pct: number } => !!point.at && Number.isFinite(point.pct))
    .sort((a, b) => a.at.getTime() - b.at.getTime());
  if (!points.length) return null;
  if (on.getTime() <= points[0].at.getTime()) return points[0].pct;
  const last = points[points.length - 1];
  if (on.getTime() >= last.at.getTime()) return last.pct;
  for (let index = 1; index < points.length; index += 1) {
    const before = points[index - 1];
    const after = points[index];
    if (on.getTime() <= after.at.getTime()) {
      const span = after.at.getTime() - before.at.getTime();
      const ratio = span > 0 ? (on.getTime() - before.at.getTime()) / span : 0;
      return Math.round(before.pct + (after.pct - before.pct) * ratio);
    }
  }
  return last.pct;
}

const ASK_LABEL: Record<string, string> = {
  long_sleeve: '長袖', light_outer: '薄外套', specific_sku: '指定款', size_missing: '缺尺寸',
};

// 「客人問過：長袖 5 · 薄外套 2」 — R5's asks, most asked first.
export function asksSummary(asks?: Record<string, number> | null): string {
  const rows = Object.keys(asks || {})
    .map((kind) => ({ kind, count: Number((asks || {})[kind]) || 0 }))
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);
  if (!rows.length) return '';
  return rows.map((row) => `${ASK_LABEL[row.kind] || row.kind} ${row.count}`).join(' · ');
}
